import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Loader2, TrendingUp, TrendingDown, ShoppingBag, Package, Users,
  AlertTriangle, ArrowRight, DollarSign, Truck, Clock, RefreshCw, CheckCircle2,
  Download, Upload
} from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';
import { adminAPI } from '@/utils/adminApi';
import { formatCOP } from '@/utils/format';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ label, value, sub, icon: Icon, tone = 'green', testid }) => {
  const tones = {
    green: 'bg-[#2A5C43] text-white',
    orange: 'bg-[#D47A4A] text-white',
    dark: 'bg-[#1C1917] text-white',
    cream: 'bg-[#F7F2E8] text-[#1C1917] border border-[#E7E5E4]',
  };
  return (
    <div className={`rounded-3xl p-6 ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-start justify-between mb-4">
        <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${
          tone === 'cream' ? 'bg-white' : 'bg-white/15'
        }`}>
          <Icon className={`w-5 h-5 ${tone === 'cream' ? 'text-[#2A5C43]' : 'text-white'}`} />
        </div>
      </div>
      <p className={`text-xs uppercase tracking-[0.2em] mb-2 font-semibold ${
        tone === 'cream' ? 'text-[#8BA58F]' : 'text-white/70'
      }`}>{label}</p>
      <p className="font-display font-bold text-3xl mb-1">{value}</p>
      {sub && <p className={`text-xs ${tone === 'cream' ? 'text-[#57534E]' : 'text-white/70'}`}>{sub}</p>}
    </div>
  );
};

const AdminHome = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [diag, setDiag] = useState(null);
  const [reseeding, setReseeding] = useState(false);
  const [reseedMsg, setReseedMsg] = useState('');

  const loadAll = async () => {
    try {
      const [statsData, diagData] = await Promise.all([
        adminAPI.getStats(),
        axios.get(`${API}/admin/catalog/diagnostic`, { withCredentials: true }).then(r => r.data),
      ]);
      setStats(statsData);
      setDiag(diagData);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const handleReseed = async ({ force = false } = {}) => {
    if (force && !window.confirm('⚠️ Modo forzado: se borrarán todos los productos, marcas, categorías y tarifas de envío, y se volverán a crear desde el catálogo original. ¿Continuar?')) {
      return;
    }
    setReseeding(true);
    setReseedMsg('');
    try {
      const { data } = await axios.post(
        `${API}/admin/catalog/reseed?force=${force ? 'true' : 'false'}&reactivate_all=true`,
        {},
        { withCredentials: true }
      );
      setReseedMsg(`✅ Restaurado. Productos activos: ${data.after.active}/${data.after.products}`);
      await loadAll();
    } catch (e) {
      setReseedMsg(`❌ ${e.response?.data?.detail || 'Error al restaurar'}`);
    } finally {
      setReseeding(false);
    }
  };

  const catalogBroken = diag && (diag.active_products === 0 || diag.total_products === 0);
  const partialInactive = diag && diag.inactive_products > 0 && diag.active_products > 0;

  // ---- Export / Import ----
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState('');
  const [importMode, setImportMode] = useState('upsert');
  const importInputRef = React.useRef(null);

  const handleExport = () => {
    // Trigger download via cookie-authed browser request
    window.location.href = `${API}/admin/catalog/export`;
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.json')) {
      setImportMsg('❌ El archivo debe ser un .json exportado desde /admin');
      return;
    }
    if (importMode === 'replace' && !window.confirm(
      '⚠️ Modo REEMPLAZAR: se borrarán TODOS los productos, marcas, categorías, envíos y archivos actuales y se sustituirán por los del archivo. ¿Continuar?'
    )) {
      e.target.value = '';
      return;
    }
    setImporting(true);
    setImportMsg('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(
        `${API}/admin/catalog/import?mode=${importMode}`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000 }
      );
      const parts = Object.entries(data.results || {}).map(
        ([k, v]) => `${k}: ${v.after} (+${v.inserted}, ↻${v.updated})`
      );
      setImportMsg(`✅ Importado desde ${data.source || 'archivo'}. ${parts.join(' • ')}`);
      await loadAll();
    } catch (err) {
      setImportMsg(`❌ ${err.response?.data?.detail || err.message || 'Error al importar'}`);
    } finally {
      setImporting(false);
      if (importInputRef.current) importInputRef.current.value = '';
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <p className="text-xs tracking-[0.25em] uppercase text-[#D47A4A] font-semibold mb-2">Panel administrativo</p>
          <h1 className="font-display font-bold text-3xl sm:text-4xl text-[#1C1917] tracking-tight" data-testid="admin-dashboard-title">Dashboard</h1>
          <p className="text-[#57534E] mt-2">Visión general de tu tienda</p>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-[#2A5C43]" />
          </div>
        )}

        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Catalog health widget — always visible for admins */}
        {diag && (
          <div
            className={`rounded-3xl border p-6 mb-6 ${
              catalogBroken ? 'bg-red-50 border-red-200' :
              partialInactive ? 'bg-orange-50 border-orange-200' :
              'bg-white border-[#E7E5E4]'
            }`}
            data-testid="catalog-health-widget"
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                {catalogBroken ? (
                  <AlertTriangle className="w-6 h-6 text-red-600 shrink-0 mt-0.5" />
                ) : partialInactive ? (
                  <AlertTriangle className="w-6 h-6 text-orange-600 shrink-0 mt-0.5" />
                ) : (
                  <CheckCircle2 className="w-6 h-6 text-[#2A5C43] shrink-0 mt-0.5" />
                )}
                <div>
                  <p className="font-display font-semibold text-[#1C1917] mb-1">
                    {catalogBroken
                      ? 'Catálogo vacío o sin productos activos'
                      : partialInactive
                      ? `${diag.inactive_products} productos están inactivos`
                      : 'Catálogo saludable'}
                  </p>
                  <p className="text-xs text-[#57534E]" data-testid="catalog-diag-summary">
                    Productos: <b>{diag.active_products}</b> activos / {diag.total_products} totales •
                    Perros: <b>{diag.perros_active}</b> • Gatos: <b>{diag.gatos_active}</b> •
                    Marcas: {diag.brands} • Categorías: {diag.categories} • Zonas envío: {diag.shipping_rates}
                  </p>
                  {reseedMsg && (
                    <p className="text-xs mt-2 font-medium" data-testid="reseed-message">{reseedMsg}</p>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleReseed({ force: false })}
                  disabled={reseeding}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold bg-[#2A5C43] text-white hover:bg-[#214A35] disabled:opacity-50"
                  data-testid="btn-reactivate-catalog"
                >
                  {reseeding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  Reactivar + rellenar
                </button>
                {catalogBroken && (
                  <button
                    onClick={() => handleReseed({ force: true })}
                    disabled={reseeding}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold border border-red-600 text-red-600 hover:bg-red-600 hover:text-white disabled:opacity-50"
                    data-testid="btn-force-reseed"
                  >
                    Forzar recreación
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Migración de catálogo entre entornos */}
        <div className="bg-white rounded-3xl border border-[#E7E5E4] p-6 mb-6" data-testid="catalog-migration-widget">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-3">
            <div>
              <p className="font-display font-semibold text-[#1C1917] mb-1">Migración de catálogo</p>
              <p className="text-xs text-[#57534E]">
                Exporta el catálogo completo (productos, marcas, categorías, envíos y archivos) e impórtalo en otro entorno. Las imágenes se conservan porque el almacenamiento es compartido.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <button
              onClick={handleExport}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold bg-[#2A5C43] text-white hover:bg-[#214A35]"
              data-testid="btn-export-catalog"
            >
              <Download className="w-3.5 h-3.5" /> Exportar JSON
            </button>

            <div className="inline-flex items-center gap-2 border border-[#E7E5E4] rounded-full px-3 py-1.5">
              <label className="text-xs text-[#57534E]">Modo:</label>
              <select
                value={importMode}
                onChange={(e) => setImportMode(e.target.value)}
                className="text-xs font-semibold bg-transparent outline-none"
                data-testid="import-mode-select"
              >
                <option value="upsert">Combinar (seguro)</option>
                <option value="replace">Reemplazar (borra y sustituye)</option>
              </select>
            </div>

            <button
              onClick={() => importInputRef.current?.click()}
              disabled={importing}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold bg-[#D47A4A] text-white hover:bg-[#B86538] disabled:opacity-50"
              data-testid="btn-import-catalog"
            >
              {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {importing ? 'Importando...' : 'Importar JSON'}
            </button>
            <input
              ref={importInputRef}
              type="file"
              accept="application/json,.json"
              onChange={handleImportFile}
              className="hidden"
              data-testid="import-file-input"
            />
          </div>

          {importMsg && (
            <p
              className={`text-xs mt-3 font-medium ${importMsg.startsWith('❌') ? 'text-red-700' : 'text-[#2A5C43]'}`}
              data-testid="import-message"
            >
              {importMsg}
            </p>
          )}

          <div className="mt-4 pt-4 border-t border-[#F5F5F4] text-xs text-[#57534E] space-y-1">
            <p><strong>Cómo migrar preview → producción:</strong></p>
            <ol className="list-decimal list-inside space-y-0.5 pl-2">
              <li>Entra al admin del <b>preview</b> y pulsa "Exportar JSON" — se descargará un archivo.</li>
              <li>Entra al admin de <b>producción</b>, elige el modo (Combinar es lo más seguro) y pulsa "Importar JSON".</li>
              <li>Selecciona el archivo descargado y espera unos segundos.</li>
            </ol>
          </div>
        </div>

        {stats && !loading && (
          <>
            {/* Revenue big card */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4" data-testid="admin-revenue-cards">
              <div className="lg:col-span-2 bg-gradient-to-br from-[#2A5C43] to-[#1e4230] rounded-3xl p-8 text-white">
                <p className="text-xs uppercase tracking-[0.2em] text-white/70 font-semibold mb-2">Ingresos de este mes</p>
                <p className="font-display font-bold text-5xl mb-4" data-testid="stat-revenue-month">{formatCOP(stats.revenue_month)}</p>
                <div className="flex items-center gap-4 text-sm">
                  {stats.revenue_growth_pct >= 0 ? (
                    <span className="flex items-center gap-1 text-green-300">
                      <TrendingUp className="w-4 h-4" /> {stats.revenue_growth_pct}%
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-red-300">
                      <TrendingDown className="w-4 h-4" /> {stats.revenue_growth_pct}%
                    </span>
                  )}
                  <span className="text-white/70">vs. mes anterior ({formatCOP(stats.revenue_prev_month)})</span>
                </div>
                <div className="mt-6 pt-6 border-t border-white/10 grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-white/60 mb-1">Hoy</p>
                    <p className="font-display font-bold text-xl">{formatCOP(stats.revenue_today)}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-white/60 mb-1">Total histórico</p>
                    <p className="font-display font-bold text-xl">{formatCOP(stats.revenue_all)}</p>
                  </div>
                </div>
              </div>
              <StatCard label="Pedidos hoy" value={stats.orders_today} sub={`${stats.total_orders} totales`} icon={ShoppingBag} tone="orange" testid="stat-orders-today" />
            </div>

            {/* KPI row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <StatCard label="Pedidos pendientes" value={stats.orders_pending} sub="Requieren atención" icon={Clock} tone="cream" testid="stat-orders-pending" />
              <StatCard label="En preparación" value={stats.orders_processing} sub="Pagados o procesando" icon={Package} tone="cream" testid="stat-orders-processing" />
              <StatCard label="Enviados" value={stats.orders_shipped} sub="En tránsito" icon={Truck} tone="cream" testid="stat-orders-shipped" />
              <StatCard label="Bajo stock" value={stats.low_stock_count} sub="Variantes ≤ 5 unidades" icon={AlertTriangle} tone="cream" testid="stat-low-stock" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Low stock */}
              <div className="bg-white rounded-3xl border border-[#E7E5E4] overflow-hidden">
                <div className="p-6 border-b border-[#F5F5F4] flex items-center justify-between">
                  <div>
                    <h3 className="font-display font-semibold text-lg text-[#1C1917]">Bajo stock</h3>
                    <p className="text-xs text-[#57534E]">Variantes con 5 o menos unidades</p>
                  </div>
                  <Link to="/admin/productos" className="text-xs font-medium text-[#2A5C43] hover:underline flex items-center gap-1" data-testid="link-manage-inventory">
                    Gestionar <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
                <div className="divide-y divide-[#F5F5F4] max-h-80 overflow-y-auto" data-testid="low-stock-list">
                  {stats.low_stock.length === 0 && (
                    <p className="p-6 text-sm text-[#57534E] text-center">Todos los productos tienen stock suficiente 🎉</p>
                  )}
                  {stats.low_stock.map((item) => (
                    <div key={item.variant_id} className="p-4 flex items-center justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm text-[#1C1917] truncate">{item.product_name}</p>
                        <p className="text-xs text-[#57534E]">{item.brand} • {item.size} • SKU {item.sku}</p>
                      </div>
                      <div className={`text-right ml-4 px-3 py-1 rounded-full text-xs font-semibold ${
                        item.stock === 0 ? 'bg-red-50 text-red-700' : 'bg-orange-50 text-orange-700'
                      }`}>
                        {item.stock === 0 ? 'Agotado' : `${item.stock} unid.`}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top products */}
              <div className="bg-white rounded-3xl border border-[#E7E5E4] overflow-hidden">
                <div className="p-6 border-b border-[#F5F5F4] flex items-center justify-between">
                  <div>
                    <h3 className="font-display font-semibold text-lg text-[#1C1917]">Más vendidos</h3>
                    <p className="text-xs text-[#57534E]">Top 5 variantes por unidades</p>
                  </div>
                  <Link to="/admin/pedidos" className="text-xs font-medium text-[#2A5C43] hover:underline flex items-center gap-1" data-testid="link-manage-orders">
                    Ver pedidos <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
                <div className="divide-y divide-[#F5F5F4]" data-testid="top-products-list">
                  {stats.top_products.length === 0 && (
                    <p className="p-6 text-sm text-[#57534E] text-center">Aún no hay ventas registradas</p>
                  )}
                  {stats.top_products.map((p, i) => (
                    <div key={p.sku || i} className="p-4 flex items-center gap-4">
                      <div className="w-8 h-8 rounded-full bg-[#F7F2E8] flex items-center justify-center font-display font-bold text-[#2A5C43]">
                        {i + 1}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm text-[#1C1917] truncate">{p.product_name}</p>
                        <p className="text-xs text-[#57534E]">{p.size} • SKU {p.sku}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-display font-bold text-[#1C1917]">{p.units_sold}</p>
                        <p className="text-xs text-[#57534E]">unidades</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Extra stats */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white rounded-2xl border border-[#E7E5E4] p-5 flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-[#F7F2E8] flex items-center justify-center">
                  <Package className="w-5 h-5 text-[#2A5C43]" />
                </div>
                <div>
                  <p className="text-xs text-[#57534E]">Productos activos</p>
                  <p className="font-display font-bold text-2xl text-[#1C1917]" data-testid="stat-total-products">{stats.total_products}</p>
                </div>
              </div>
              <div className="bg-white rounded-2xl border border-[#E7E5E4] p-5 flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-[#F7F2E8] flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#D47A4A]" />
                </div>
                <div>
                  <p className="text-xs text-[#57534E]">Clientes registrados</p>
                  <p className="font-display font-bold text-2xl text-[#1C1917]" data-testid="stat-total-customers">{stats.total_customers}</p>
                </div>
              </div>
              <div className="bg-white rounded-2xl border border-[#E7E5E4] p-5 flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-[#F7F2E8] flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-[#8BA58F]" />
                </div>
                <div>
                  <p className="text-xs text-[#57534E]">Ticket promedio</p>
                  <p className="font-display font-bold text-2xl text-[#1C1917]">
                    {stats.total_orders > 0 ? formatCOP(stats.revenue_all / stats.total_orders) : formatCOP(0)}
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminHome;
